import React, { useEffect } from 'react';
import {
    ActionIcon,
    Button,
    Flex,
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconArrowRight, IconPlus, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { theme } from '../../../global/style/theme.tsx';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import {
    useScenarios,
    useDeleteScenario,
} from '../../../global/entities/Scenario/queries/useScenarios.ts';
import { IScenarioSummary } from '../../../global/entities/Scenario/Scenario.ts';

const ScenarioTable: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { data: scenarios, isLoading, refetch } = useScenarios();
    const { mutateAsync: deleteScenario } = useDeleteScenario();
    const navigate = useNavigate();
    const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();

    useEffect(() => {
        setSortedData([]);
        if (scenarios?.length) setSortedData(scenarios);
    }, [scenarios]);

    const openModalDelete = (scenarioId: number) =>
        modals.openConfirmModal({
            title: t('modal.deleteItemModal.title'),
            children: (
                <Stack gap="sm">
                    <Text>{t('modal.deleteItemModal.question')}</Text>
                    <Text fw="bold">{t('modal.deleteItemModal.details')}</Text>
                </Stack>
            ),
            labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
            confirmProps: { color: 'red' },
            cancelProps: { variant: 'outline', color: 'gray' },
            onConfirm: async () => {
                await deleteScenario(scenarioId);
                refetch();
            },
            size: 'lg',
            radius: 'xs',
        });

    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

    if (isLoading)
        return <Loading visible={true} loadingMessage={t('common.loading')} />;

    return (
        <>
            <Flex mb="lg" mt="lg" align="center" justify="space-between">
                <Title c="black" order={4}>
                    {t('scenario.title')}
                </Title>
                <Flex gap="sm">
                    <Button
                        onClick={() => navigate('/adminDashboard/scenarios/0')}
                        leftSection={<IconPlus />}
                    >
                        {t('scenario.addNew')}
                    </Button>
                </Flex>
            </Flex>
            <Paper w="100%">
                <Stack gap="md">
                    <ScrollArea w="100%">
                        <Table verticalSpacing="xs">
                            <Table.Thead>
                                <Table.Tr>
                                    <Th
                                        sorted={sortBy === (lang === 'fr' ? 'nameFr' : 'nameEn')}
                                        reversed={reverseSortDirection}
                                        onSort={() =>
                                            setSorting(lang === 'fr' ? 'nameFr' : 'nameEn', sortedData)
                                        }
                                    >
                                        {t('formGlobal.name')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'slotCount'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('slotCount', sortedData)}
                                    >
                                        {t('scenario.slotCount')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'isActive'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('isActive', sortedData)}
                                    >
                                        {t('formGlobal.active')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'lastModifiedDate'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('lastModifiedDate', sortedData)}
                                    >
                                        {t('formGlobal.lastModified')}
                                    </Th>
                                    <Table.Th></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {sortedData?.map((scenario: IScenarioSummary, index: number) => (
                                    <Table.Tr
                                        key={scenario.id}
                                        style={{
                                            background:
                                                index % 2 === 1
                                                    ? undefined
                                                    : 'var(--Background-Gray-light, #E9ECEF)',
                                        }}
                                    >
                                        <Table.Td>
                                            <Text fw={500} fz={14} truncate maw={300}>
                                                {lang === 'fr' ? scenario.nameFr : scenario.nameEn}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text fw={500} fz={14}>
                                                {scenario.slotCount}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text fw={500} fz={14}>
                                                {scenario.isActive ? t('common.yes') : t('common.no')}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text fw={500} fz={14}>
                                                {scenario.lastModifiedDate
                                                    ? new Date(scenario.lastModifiedDate).toLocaleString()
                                                    : ''}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Flex align="center">
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    onClick={() => openModalDelete(scenario.id)}
                                                >
                                                    <IconTrash size={18} />
                                                </ActionIcon>
                                                <ActionIcon variant="transparent">
                                                    {isLoading ? (
                                                        <Loader size="xs" color={theme.colors?.blue?.toString()} />
                                                    ) : (
                                                        <IconArrowRight
                                                            onClick={() =>
                                                                navigate(`/adminDashboard/scenarios/${scenario.id}`)
                                                            }
                                                            color={theme.colors?.blue?.toString()}
                                                        />
                                                    )}
                                                </ActionIcon>
                                            </Flex>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Stack>
            </Paper>
        </>
    );
};

const WrappedScenarioTable: React.FC = () => (
    <TableContextProvider>
        <ScenarioTable />
    </TableContextProvider>
);

export default WrappedScenarioTable;
